import { useMediaProps } from '@zapier/responsive-utils';
import { addParamsToUrl } from '@zapier/url-utils';
import { SplitFactory } from '@splitsoftware/splitio';
import React, { useContext, createContext } from 'react';

/**
 * Borrowed from team Monitor: https://github.com/zapier/reporting/blob/a623f082660c9ee1d1040ee669c5dbc9cd0fd778/packages/reporting/src/utils/buildStaticAssetUrl/buildStaticAssetUrl.ts
 */
const STATIC_ASSET_URL = 'https://cdn.zapier.com/zapier/';
/**
 * Make sure the URL is valid.
 *
 * We make sure to dedupe some URL parts:
 * - The provided `staticPath` is generally suffixed with a `/`.
 * - Some legacy components are still prefixing assets with `/static`.
 */

const sanitizeUrl = url => url.replace(/(\/){2,}/g, '$1').replace(/(https?:\/)/g, '$1/').replace(/(\/static){2,}/g, '$1');
/**
 * Check if a given URL should remain unprefixed.
 */


const shouldRemainUnprefixed = function () {
  let url = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return ['http', 'data:', '/media/storage', 'blob:'].some(beginning => (url || '').indexOf(beginning) === 0);
};

const buildStaticAssetUrl = function (url) {
  let staticPath = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : STATIC_ASSET_URL;
  return shouldRemainUnprefixed(url) ? url : sanitizeUrl("".concat(staticPath, "/").concat(url));
};

const isProduction = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';

// Avoid applying a `max-width` so that we can allow a cascading effect to
// happen, e.g. tablet styles will apply if desktop styles are not provided.
const mediaQueries = {
  mobile: "@media (min-width: 0)",
  tablet: "@media (min-width: 660px)",
  desktop: "@media (min-width: 1024px)"
};
const getResponsiveStyles = styles => {
  return [{
    [mediaQueries.mobile]: styles.mobile || {}
  }, {
    [mediaQueries.tablet]: styles.tablet || {}
  }, {
    [mediaQueries.desktop]: styles.desktop || {}
  }];
};
const visibleOnlyOnMobile = getResponsiveStyles({
  mobile: {
    display: 'inherit',
    visibility: 'visible'
  },
  tablet: {
    display: 'none',
    visibility: 'hidden'
  },
  desktop: {
    display: 'none',
    visibility: 'hidden'
  }
});
const visibleOnlyOnTablet = getResponsiveStyles({
  mobile: {
    display: 'none',
    visibility: 'hidden'
  },
  tablet: {
    display: 'inherit',
    visibility: 'visible'
  },
  desktop: {
    display: 'none',
    visibility: 'hidden'
  }
});
const visibleOnlyOnDesktop = getResponsiveStyles({
  mobile: {
    display: 'none',
    visibility: 'hidden'
  },
  tablet: {
    display: 'none',
    visibility: 'hidden'
  },
  desktop: {
    display: 'inherit',
    visibility: 'visible'
  }
});
const hiddenOnMobile = getResponsiveStyles({
  mobile: {
    display: 'none',
    visibility: 'hidden'
  },
  tablet: {
    display: 'inherit',
    visibility: 'visible'
  },
  desktop: {
    display: 'inherit',
    visibility: 'visible'
  }
});
const hiddenOnDesktop = getResponsiveStyles({
  mobile: {
    display: 'inherit',
    visibility: 'visible'
  },
  tablet: {
    display: 'inherit',
    visibility: 'visible'
  },
  desktop: {
    display: 'none',
    visibility: 'hidden'
  }
});

function isBrowser() {
  return typeof window !== 'undefined';
}

function isCypress() {
  var _window$top;

  // @ts-ignore Cypress will be on window if we are in a test
  return isBrowser() && ((_window$top = window.top) === null || _window$top === void 0 ? void 0 : _window$top.Cypress) !== undefined;
}

function isInIframe() {
  if (!isBrowser()) return false;

  try {
    var _window$top, _window$top2;

    // Do not show cypress tests as in iFrame
    if ( // @ts-ignore Cypress will be on window if we are in a test
    ((_window$top = window.top) === null || _window$top === void 0 ? void 0 : _window$top.Cypress) !== undefined || // @ts-ignore __STORYBOOK_ADDONS will be on window if we are in storybook
    ((_window$top2 = window.top) === null || _window$top2 === void 0 ? void 0 : _window$top2.__STORYBOOKADDONS__) !== undefined) return false;
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

const RE_ABSOLUTE_URL = /^(https?:)?\/\//;
/**
 * Resolve `url` to an absolute URL.
 *
 * It prepends `baseUrl` or falls back to `location.origin` otherwise.
 */

function resolveUrl(url, baseUrl) {
  if (RE_ABSOLUTE_URL.exec(url)) return url;
  const prefix = baseUrl || location.origin;
  const sep = url[0] === '/' ? '' : '/';
  return "".concat(prefix).concat(sep).concat(url);
}

/** URLS used throughout packages */
const urls = {
  ACCOUNT_DISCOVERY_URL: '/api/v4/account-discovery',
  ACCOUNT_LOCKED_URL: '/app/login/locked',
  ACTIVATE_ACCOUNT_URL: '/app/login/activate',
  COOKIE_ERROR_URL: '/app/login/cookies-disabled',
  COMPANY_PLAN_URL: '/companies',
  TEAMS_COMPANIES_PLAN_URL: '/teams-companies',
  CSRF_URL: '/api/v3/csrf',
  CURRENT_SESSION_URL: '/api/v4/session',
  DEFAULT_LOGIN_NEXT: '/app/dashboard',
  DEFAULT_SIGNUP_NEXT: '/app/onboarding',
  DOMAIN_DISCOVERY_URL: '/api/v4/domain-discovery',
  IFRAME_DEFAULT_SIGNUP_NEXT: '/app/dashboard',
  FORGOT_PASSWORD_URL: '/app/login/forgot',
  FORGOT_PASSWORD_API_URL: '/api/v3/accounts/password/forgot',
  HELP_URL: '/app/get-help',
  INDEX_URL: '/',
  INVITATION_API_URL: '/api/v4/invitations',
  LEGAL_URL: '/legal',
  LOGIN_API_URL: '/api/v3/login',
  LOGIN_URL: '/app/login',
  LOGIN_WITH_SSO_URL: '/app/login/sso',
  LOGOUT_API_URL: '/api/logout',
  MFA_URL: '/app/login/2fa',
  PRIVACY_URL: '/privacy',
  RESET_PASSWORD_API_URL: '/api/v3/accounts/password/reset',
  RESET_PASSWORD_URL: '/app/login/reset-password',
  SIGNUP_API_URL: '/api/v3/profiles',
  SIGNUP_URL: '/sign-up',
  SINGLE_SESSION_URL: '/app/login/secure-session',
  TWO_FACTOR_AUTH_API_URL: '/api/v3/2fa',
  TWO_FACTOR_URL: '/app/login/2fa',
  ZAP_TEMPLATE_API_URL: '/api/v4/guided-recipes',
  SECURITY_HEADERS_URL: '/api/v4/security/domain/headers/',
  REDIRECT_URL: '/redirect'
};

const queries = {
  desktop: "(min-width: 1024px)",
  tablet: "(min-width: 660px) and (max-width: 1023px)"
};
function useSize() {
  return useMediaProps({
    all: {
      size: 'small'
    },
    [queries.tablet]: {
      size: 'medium'
    },
    [queries.desktop]: {
      size: 'large'
    }
  }).size;
}

function resolveSignUpNextUrl() {
  let nextUrl = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : undefined;
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let context = arguments.length > 2 ? arguments[2] : undefined;

  if (isInIframe()) {
    return nextUrl || urls.IFRAME_DEFAULT_SIGNUP_NEXT;
  }

  if (nextUrl && context !== null && context !== void 0 && context.skipOnboarding) {
    return addParamsToUrl(urls.REDIRECT_URL, {
      next: nextUrl
    });
  }

  const nextQueryParams = {
    next: nextUrl,
    selected_apis: params.selected_apis,
    invitation_code: params.invitation_code,
    from: params.from
  };
  const baseOnboardingUrl = urls.DEFAULT_SIGNUP_NEXT; // add only truthy params

  return addParamsToUrl(baseOnboardingUrl, nextQueryParams, true);
}

let splitInstance;
/**
 * Returns an instance of the Split SDK.
 */

function getServerSideSplitSingleton() {
  var _process$env$SPLIT_DE;

  // Configuration
  const SPLIT_READY_TIMEOUT = process.env.SPLIT_READY_TIMEOUT ? parseInt(process.env.SPLIT_READY_TIMEOUT) : 5;
  const NEXT_PUBLIC_SPLIT_API_KEY = process.env.NEXT_PUBLIC_SPLIT_API_KEY;
  const SPLIT_FEATURES_PATH = process.env.SPLIT_FEATURES_PATH;
  const SPLIT_DEBUG = ((_process$env$SPLIT_DE = process.env.SPLIT_DEBUG) === null || _process$env$SPLIT_DE === void 0 ? void 0 : _process$env$SPLIT_DE.toLowerCase()) === 'true';

  if (!NEXT_PUBLIC_SPLIT_API_KEY) {
    throw new Error('Missing configuration for NEXT_PUBLIC_SPLIT_API_KEY.');
  }

  if (isBrowser()) {
    throw new Error('getServerSideSplitSingleton() should only be used server-side.');
  }

  if (!splitInstance) {
    splitInstance = SplitFactory({
      core: {
        authorizationKey: NEXT_PUBLIC_SPLIT_API_KEY
      },
      features: SPLIT_FEATURES_PATH !== '' ? SPLIT_FEATURES_PATH : undefined,
      startup: {
        readyTimeout: SPLIT_READY_TIMEOUT
      },
      debug: SPLIT_DEBUG
    });
  }

  return splitInstance;
}
/**
 * Returns an identifier to pass as the "key" to Split SDK impressions.
 * In order:
 * - splitId query param: used for QA testing since these pages are unathenticated
 * - visitor_id cookie: Set automatically by Cloudfront function in staging/production.
 * - '0': Default value
 * @param context
 * @returns {String}
 */

function getServerSideSplitId(context) {
  return String(context.query.splitId || context.req.cookies['visitor_id'] || '0');
}

const FeatureFlagContext = /*#__PURE__*/createContext({
  treatments: {}
});
/**
 * Returns the active treatment for a specific feature flag.
 */

const useFeatureFlag = featureName => {
  const context = useContext(FeatureFlagContext);
  return context.treatments[featureName];
};
/**
 * Provider to access treatments within child components.
 */

const FeatureFlagProvider = _ref => {
  let {
    treatments,
    children
  } = _ref;
  return children ? /*#__PURE__*/React.createElement(FeatureFlagContext.Provider, {
    value: {
      treatments
    }
  }, children) : null;
};

const FEATURE_FLAGS = {
  NLA_EMBED_REDIRECT: 'nla_embed_redirect',
  COMPANY_SALES_REDIRECT: 'experiment_company-card-contact-sales_zapier',
  TPS_EXPERIMENT_SIGNUP_LANDING_PAGE: 'tps_experiment_signup_landing_page'
};

function isNLAPluginSignUp(utmCampaign) {
  return Array.isArray(utmCampaign) ? utmCampaign[0] === 'openai' : utmCampaign === 'openai';
}

export { FEATURE_FLAGS, FeatureFlagProvider, STATIC_ASSET_URL, buildStaticAssetUrl, getResponsiveStyles, getServerSideSplitId, getServerSideSplitSingleton, hiddenOnDesktop, hiddenOnMobile, isBrowser, isCypress, isInIframe, isNLAPluginSignUp, isProduction, mediaQueries, resolveSignUpNextUrl, resolveUrl, urls, useFeatureFlag, useSize, visibleOnlyOnDesktop, visibleOnlyOnMobile, visibleOnlyOnTablet };
